/**
 * Capa de servicio de "Plantillas bancarias" (disenador YAML).
 *
 * - Commands y queries de plantillas: API del modulo de payroll (contexto
 *   bank-file), montado en /documents/bank-templates. La validacion del YAML
 *   y el dry-run son SIEMPRE del backend; el front no parsea YAML.
 * - Catalogos (paises / bancos con UUID reales): API legado via get-table.
 */
import { getData, TableDataResponse } from '../get-table'
import { HttpService } from '../http.service'
import { API_PAYROLL, ENV } from '../../env'
import { useUserStore } from '@/store/user'
import {
  RawCreateResult,
  RawDetail,
  RawDryRun,
  RawEditorSupport,
  RawListItem,
  RawValidation,
  toDryRunResult,
  toEditorSupport,
  toSummary,
  toTemplate,
  toValidationResult
} from './adapters'
import {
  BankOption,
  BankTemplate,
  BankTemplateApiError,
  BankTemplateDocumentation,
  BankTemplateSummary,
  DryRunResult,
  EditorSupport,
  SelectOption,
  ValidationResult
} from './types'

export * from './types'
export { buildBaseYaml } from './base-yaml'

/* API GW del modulo de payroll (mismo patron que payment-slips-export) */
const httpPayroll = new HttpService(API_PAYROLL[ENV()]())

/* Ruta base del router bank-templates dentro del API de payroll */
const BASE_PATH = 'documents/bank-templates'

/* Headers que exige el modulo de payroll (workspace explicito) */
const payrollHeaders = () => ({
  'x-workspace-id': useUserStore().user?.workspaceId ?? ''
})

/**
 * Errores del backend llegan como { code, message, params }. Se relanzan como
 * BankTemplateApiError para que las vistas muestren el mensaje de dominio.
 */
const toApiError = (error: any): Error => {
  const body = error?.response?.data
  if (body?.message)
    return new BankTemplateApiError(body.message, [], body.code, body.params)
  return error instanceof Error ? error : new Error(String(error))
}

/* ------------------------------------------------------------------------- */
/* Catalogos (API legado, UUIDs reales)                                       */
/* ------------------------------------------------------------------------- */

interface CountryRow {
  id: string
  name: string
}

export async function getCountryOptions(): Promise<SelectOption[]> {
  const result = await getData<[TableDataResponse<CountryRow>]>({
    tables: ['pg_countries'],
    fields: ['id', 'name'],
    modifier: ['ORDER BY name'],
    options: { outputFormat: 'json' }
  })
  return (result[0]?.results ?? []).map(row => ({
    id: row.id,
    name: row.name
  }))
}

interface BankRow {
  id: string
  name: string
  code: string
}

/* Bancos del pais indicado; sin pais devuelve todos (para resolver nombres) */
export async function getBankOptions(
  countryId?: string
): Promise<BankOption[]> {
  const result = await getData<[TableDataResponse<BankRow>]>({
    tables: ['pg_banks'],
    fields: ['id', 'name', 'code'],
    where: countryId ? [`country_id = '${countryId}'`] : [],
    modifier: ['ORDER BY name'],
    options: { outputFormat: 'json' }
  })
  return (result[0]?.results ?? []).map(row => ({
    id: row.id,
    name: row.name,
    code: row.code
  }))
}

/* ------------------------------------------------------------------------- */
/* Soporte del editor                                                         */
/* ------------------------------------------------------------------------- */

export async function getEditorSupport(): Promise<EditorSupport> {
  const response = await httpPayroll.get<RawEditorSupport>(
    `${BASE_PATH}/editor-support`,
    { headers: payrollHeaders() }
  )
  return toEditorSupport(response?.data ?? {})
}

export async function getTemplateDocumentation(
  signal?: AbortSignal
): Promise<BankTemplateDocumentation> {
  const response = await httpPayroll.get<BankTemplateDocumentation>(
    `${BASE_PATH}/documentation`,
    { headers: payrollHeaders(), signal }
  )
  return response.data
}

/* ------------------------------------------------------------------------- */
/* Plantillas                                                                 */
/* ------------------------------------------------------------------------- */

/**
 * Listado completo de plantillas del workspace, sin depender del pais de la
 * nomina activa: `allCountries=true` le indica al backend que ignore el header
 * `x-country-id` que inyecta HttpService. Los filtros (pais, nombre e
 * inactivas) se aplican en la vista sobre este listado.
 */
export async function getTemplates(): Promise<BankTemplateSummary[]> {
  /* La lista trae UUIDs; los nombres visibles salen de los catalogos */
  const [response, countries, banks] = await Promise.all([
    httpPayroll.get<RawListItem[]>(BASE_PATH, {
      params: { allCountries: true },
      headers: payrollHeaders()
    }),
    getCountryOptions(),
    getBankOptions()
  ])
  const countryNames = new Map(countries.map(item => [item.id, item.name]))
  const bankNames = new Map(banks.map(item => [item.id, item.name]))
  return (response?.data ?? []).map(item =>
    toSummary(item, countryNames, bankNames)
  )
}

export async function getTemplate(id: string): Promise<BankTemplate> {
  const response = await httpPayroll.get<RawDetail>(`${BASE_PATH}/${id}`, {
    headers: payrollHeaders()
  })
  return toTemplate(response.data)
}

export interface CreateTemplateDTO {
  name: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  yaml: string
}

/* Crea la plantilla con su primer borrador y devuelve el id asignado */
export async function createTemplate(dto: CreateTemplateDTO): Promise<string> {
  try {
    const response = await httpPayroll.post<CreateTemplateDTO, RawCreateResult>(
      BASE_PATH,
      dto,
      { headers: payrollHeaders() }
    )
    return response.data.bankTemplateId
  } catch (error) {
    throw toApiError(error)
  }
}

export interface UpdateTemplateDTO {
  name: string
  countryId: string
  originBankId: string
  destinationBankIds: string[]
  yaml: string
}

/* Actualiza la plantilla completa (datos basicos + YAML) en un solo PUT */
export async function updateTemplate(
  id: string,
  dto: UpdateTemplateDTO
): Promise<void> {
  try {
    await httpPayroll.put(`${BASE_PATH}/${id}`, dto, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

export async function deleteDraft(id: string): Promise<void> {
  try {
    await httpPayroll.delete(`${BASE_PATH}/${id}/draft`, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

export async function publishTemplate(id: string): Promise<void> {
  try {
    await httpPayroll.post(`${BASE_PATH}/${id}/publish`, null, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

/* Crea la nueva version de trabajo (borrador) desde la publicada */
export async function createWorkingVersion(id: string): Promise<void> {
  try {
    await httpPayroll.post(`${BASE_PATH}/${id}/versions`, null, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

export async function activateTemplate(id: string): Promise<void> {
  try {
    await httpPayroll.post(`${BASE_PATH}/${id}/activate`, null, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

export async function inactivateTemplate(id: string): Promise<void> {
  try {
    await httpPayroll.post(`${BASE_PATH}/${id}/inactivate`, null, {
      headers: payrollHeaders()
    })
  } catch (error) {
    throw toApiError(error)
  }
}

/* ------------------------------------------------------------------------- */
/* Validacion (en vivo, siempre del backend)                                  */
/* ------------------------------------------------------------------------- */

/**
 * Valida el YAML en el backend. Responde 200 aunque sea invalido; los issues
 * vienen en el view. `signal` permite cancelar requests obsoletos mientras
 * el usuario sigue escribiendo.
 */
export async function validateYaml(
  yaml: string,
  signal?: AbortSignal
): Promise<ValidationResult> {
  const response = await httpPayroll.post<{ yaml: string }, RawValidation>(
    `${BASE_PATH}/validate`,
    { yaml },
    { headers: payrollHeaders(), signal }
  )
  return toValidationResult(response.data)
}

/* ------------------------------------------------------------------------- */
/* Dry Run                                                                    */
/* ------------------------------------------------------------------------- */

/* Genera el archivo con el canonical sintetico del backend (requiere id) */
export async function dryRunTemplate(id: string): Promise<DryRunResult> {
  try {
    const response = await httpPayroll.post<null, RawDryRun>(
      `${BASE_PATH}/${id}/dry-run`,
      null,
      { headers: payrollHeaders() }
    )
    return toDryRunResult(response.data)
  } catch (error) {
    throw toApiError(error)
  }
}

/* Descarga el archivo del dry run (texto plano o binario base64) */
export function downloadDryRunFile(result: DryRunResult): void {
  const blob =
    result.encoding === 'base64'
      ? new Blob(
          [Uint8Array.from(atob(result.content), char => char.charCodeAt(0))],
          { type: 'application/octet-stream' }
        )
      : new Blob([result.content], { type: 'text/plain;charset=utf-8' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = result.fileName || `dry-run.${result.extension || 'txt'}`
  link.click()
  window.URL.revokeObjectURL(url)
}
